import * as React from 'react';

import { Activity, ActivityClassification } from './interfaces';
import { ACTIVITY_CLASSIFICATION_FILTERS, SAVED_ACTIVITY_FILTERS, activityClassificationGroupings } from './shared';

import { Checkbox, DISABLED, INDETERMINATE, } from '../../../../../Shared/index'

const dropdownIconSrc = `${window.postbuildEnv.CDN_URL}/images/icons/dropdown.svg`

interface Grouping {
  keys: string[],
  group: string
}

interface IndividualActivityClassificationFilterRowProps {
  activityClassificationFilters: string[],
  activityClassificationKey: string,
  handleActivityClassificationFilterChange: (activityClassificationFilters: string[]) => void,
  uniqueActivityClassifications: ActivityClassification[],
  filteredActivities: Activity[],
}

interface ActivityClassificationToggleProps {
  filteredActivities: Activity[],
  grouping: Grouping,
  uniqueActivityClassifications: ActivityClassification[],
  activityClassificationFilters: string[],
  handleActivityClassificationFilterChange: (activityClassificationFilters: string[]) => void,
  savedActivityFilters: number[],
  handleSavedActivityFilterChange: () => void,
}

interface ActivityClassificationFiltersProps {
  activities: Activity[],
  filterActivities: (ignoredKey?: string) => Activity[]
  activityClassificationFilters: string[],
  handleActivityClassificationFilterChange: (activityClassificationFilters: string[]) => void,
  handleSavedActivityFilterChange: () => void,
  savedActivityFilters: number[],
  savedActivityIds: number[],
}

const IndividualActivityClassificationFilterRow = ({ activityClassificationFilters, activityClassificationKey, handleActivityClassificationFilterChange, uniqueActivityClassifications, filteredActivities, }: IndividualActivityClassificationFilterRowProps) => {
  const activityCount = filteredActivities.filter(act => activityClassificationKey === act.activity_classification.key).length
  const selected = activityClassificationFilters.includes(activityClassificationKey)
  const disabled = activityCount === 0

  function toggleCheckbox() {
    if (selected) {
      uncheckIndividualFilter()
    } else {
      checkIndividualFilter()
    }
  }

  function checkIndividualFilter() {
    const newActivityClassificationFilters = Array.from(new Set(activityClassificationFilters.concat([activityClassificationKey])))
    handleActivityClassificationFilterChange(newActivityClassificationFilters)
  }

  function uncheckIndividualFilter() {
    const newActivityClassificationFilters = activityClassificationFilters.filter(k => k !== activityClassificationKey)
    handleActivityClassificationFilterChange(newActivityClassificationFilters)
  }

  const activityClassification = uniqueActivityClassifications.find(ac => ac.key === activityClassificationKey)

  if (!activityClassification) { return <span /> }

  return (
    <div className="individual-row filter-row" key={activityClassificationKey}>
      <div>
        <Checkbox
          ariaLabel={`Filter by ${activityClassification.alias}`}
          onClick={toggleCheckbox}
          selected={selected}
          state={disabled ? DISABLED : null}
        />
        <div className="activity-classification-data">
          <span id={activityClassification.alias}>{activityClassification.alias}</span>
          <span className="description">{activityClassification.description}</span>
          <span className="grade-text">{activityClassification.gradeText}</span>
        </div>
      </div>
      <span>({activityCount})</span>
    </div>
  )
}

const ActivityClassificationToggle = ({filteredActivities, grouping, uniqueActivityClassifications, activityClassificationFilters, handleActivityClassificationFilterChange, }: ActivityClassificationToggleProps) => {
  const [isOpen, setIsOpen] = React.useState(false)

  function toggleIsOpen() { setIsOpen(!isOpen) }

  const selected = grouping.keys.some(key => activityClassificationFilters.includes(key))
  const topLevelActivityCount = filteredActivities.filter(act => grouping.keys.includes(act.activity_classification.key)).length
  const disabled = topLevelActivityCount === 0
  const indeterminate = selected && !grouping.keys.every(key => activityClassificationFilters.includes(key))

  let checkboxState

  if (indeterminate) {
    checkboxState = INDETERMINATE
  } else if (disabled) {
    checkboxState = DISABLED
  }

  function toggleTopLevelCheckbox() {
    if (selected) {
      uncheckAllFilters()
    } else {
      checkAllFilters()
    }
  }

  function uncheckAllFilters() {
    const newActivityClassificationFilters = activityClassificationFilters.filter(key => !grouping.keys.includes(key))
    handleActivityClassificationFilterChange(newActivityClassificationFilters)
  }

  function checkAllFilters() {
    const newActivityClassificationFilters = Array.from(new Set(activityClassificationFilters.concat(grouping.keys)))
    handleActivityClassificationFilterChange(newActivityClassificationFilters)
    setIsOpen(true)
  }

  const toggleArrow = <button aria-describedby={grouping.group} aria-expanded={isOpen} aria-label={`${isOpen ? 'Hide' : 'Show'} all activity types in the category`} className="interactive-wrapper focus-on-light filter-toggle-button" onClick={toggleIsOpen} type="button"><img alt="" className={isOpen ? 'is-open' : 'is-closed'} src={dropdownIconSrc} /></button>
  const topLevelCheckbox = (
    <Checkbox
      ariaLabel={`Filter by ${grouping.group}`}
      onClick={toggleTopLevelCheckbox}
      selected={selected}
      state={checkboxState}
    />
  )

  let individualFilters = <span />
  if (isOpen) {
    individualFilters = grouping.keys.map((key: string) =>
      (<IndividualActivityClassificationFilterRow
        activityClassificationFilters={activityClassificationFilters}
        activityClassificationKey={key}
        filteredActivities={filteredActivities}
        handleActivityClassificationFilterChange={handleActivityClassificationFilterChange}
        key={key}
        uniqueActivityClassifications={uniqueActivityClassifications}
      />)
    )
  }

  return (
    <section className="toggle-section activity-classification-toggle">
      <div className="top-level filter-row">
        <div>
          {toggleArrow}
          {topLevelCheckbox}
          <span id={grouping.group}>{grouping.group}</span>
        </div>
        <span>({topLevelActivityCount})</span>
      </div>
      {grouping.new && <div className="activity-classification-new-tag">NEW</div>}
      {individualFilters}
    </section>
  )
}

const SavedRow = ({ savedActivityFilters, handleSavedActivityFilterChange, savedActivityIds, filterActivities, }) => {
  const filteredActivities = filterActivities(SAVED_ACTIVITY_FILTERS)
  const activityCount = filteredActivities.filter(act => savedActivityIds.includes(act.id)).length

  const selected = !!savedActivityFilters.length
  const disabled = activityCount === 0

  return (
    <div className="individual-row filter-row saved-row">
      <div>
        <Checkbox
          ariaLabel="Filter by Saved Activities"
          onClick={handleSavedActivityFilterChange}
          selected={selected}
          state={disabled ? DISABLED : null}
        />
        <span>Saved</span>
      </div>
      <span>({activityCount})</span>
    </div>
  )
}

const ActivityClassificationFilters = ({
  activities,
  filterActivities,
  activityClassificationFilters,
  handleActivityClassificationFilterChange,
  handleSavedActivityFilterChange,
  savedActivityFilters,
  savedActivityIds,
}: ActivityClassificationFiltersProps) => {

  const allActivityClassifications = activities.map(a => a.activity_classification)
  const uniqueActivityClassificationIds = Array.from(new Set(allActivityClassifications.map(a => a.id)))
  const uniqueActivityClassifications = uniqueActivityClassificationIds.map(id => allActivityClassifications.find(ac => ac.id === id))

  function clearAllActivityClassificationFilters() { handleActivityClassificationFilterChange([]) }

  const filteredActivities = filterActivities(ACTIVITY_CLASSIFICATION_FILTERS)

  const activityClassificationToggles = activityClassificationGroupings.map(grouping =>
    (<ActivityClassificationToggle
      activityClassificationFilters={activityClassificationFilters}
      filteredActivities={filteredActivities}
      grouping={grouping}
      handleActivityClassificationFilterChange={handleActivityClassificationFilterChange}
      key={grouping.group}
      uniqueActivityClassifications={uniqueActivityClassifications}
    />)
  )
  const clearButton = activityClassificationFilters.length ? <button className="interactive-wrapper clear-filter focus-on-light" onClick={clearAllActivityClassificationFilters} type="button">Clear</button> : <span />
  return (
    <section aria-labelledby="activitiesTitle" className="filter-section">
      <div className="name-and-clear-wrapper">
        <h2 id="activitiesTitle">Activities</h2>
        {clearButton}
      </div>
      <SavedRow
        filterActivities={filterActivities}
        handleSavedActivityFilterChange={handleSavedActivityFilterChange}
        savedActivityFilters={savedActivityFilters}
        savedActivityIds={savedActivityIds}
      />
      {activityClassificationToggles}
    </section>
  )
}

export default ActivityClassificationFilters
